import { useCallback, useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Card } from '@factorialco/f0-react'
import { Building, Clock, People, Person } from '@factorialco/f0-react/icons/app'
import * as PIXI from 'pixi.js'

import i18n from 'modules/core/lib/i18n'

import { App } from 'modules/space_control/components/WorkSpacesMap/App'
import EmptyState from 'modules/space_control/components/WorkSpacesMap/EmptyState'
import LoadingState from 'modules/space_control/components/WorkSpacesMap/LoadingState'
import { MapControls } from 'modules/space_control/components/WorkSpacesMap/MapControls'
import {
  CARD_MODE,
  CardMode,
  MAP_MODE,
  WorkSpacesMapProps,
} from 'modules/space_control/components/WorkSpacesMap/types'
import { useMapEventBus } from 'modules/space_control/contexts/workSpacesMap/MapEventBusContext'
import { useMouse } from 'modules/space_control/hooks/useMouse'
import { WorkAreaMapLocationNode } from 'modules/space_control/hooks/useWorkAreaMap'
import { useWorkSpaceUI } from 'modules/space_control/hooks/useWorkSpaceUI'
import { useWorkSpaceWithBookings } from 'modules/space_control/hooks/useWorkSpaceWithBookings'
import { Canvas } from 'modules/space_control/lib/canvas'
import { EVENT } from 'modules/space_control/lib/workSpacesMap/mapEvents'
import { buildUrl } from 'modules/core/lib/buildUrl'

export const HoveredCard = ({
  workSpaceId,
  date,
  endTime,
  startTime,
  cardMode,
}: {
  workSpaceId: number
  date: string
  endTime?: string
  startTime?: string
  cardMode: CardMode
}) => {
  const { workSpace, loading } = useWorkSpaceWithBookings({
    workSpaceId,
    date,
    endTime,
    startTime,
  })
  const spaceUI = useWorkSpaceUI(workSpace?.workSpaceTypeValue || '')

  const cardImage = workSpace?.images?.[0]?.url || spaceUI.placeholder
  const avatar = { type: 'icon' as const, icon: spaceUI.icon }

  const avatarListLength = workSpace?.bookedByEmployees?.length ?? 0

  const metadataAvatarPropertyTemplate = avatarListLength > 1 ? {
        icon: Person,
        property: {
          type: 'text' as const,
          value: workSpace?.bookedByEmployees
            ?.map((e) => e.firstName + ' ' + e.lastName)
            .join(', '),
        },
      }
    : workSpace?.bookedByEmployees?.map((e) => ({
        icon: Person,
        property: {
          type: 'person' as const,
          value: {
            firstName: e.firstName,
            lastName: e.lastName,
            src: buildUrl(e.avatar?.url),
          },
        },
      }))[0]

  const metadataAvatarProperty = metadataAvatarPropertyTemplate !== undefined ? [metadataAvatarPropertyTemplate] : []

  return (
    <>
      {loading || !workSpace ? (
        <F0Card.Skeleton />
      ) : (
        <F0Card
          title={workSpace.workSpaceName ?? ''}
          avatar={avatar}
          image={cardImage}
          metadata={
            cardMode === CARD_MODE.BOOKING
              ? [
                  {
                    icon: Building,
                    property: {
                      type: 'tag',
                      value: {
                        label: workSpace.workSpaceTypeName,
                      },
                    },
                  },
                  {
                    icon: People,
                    property: {
                      type: 'text',
                      value: i18n.tp('settings.locations.location.work_spaces.n_people', {
                        count: workSpace.originalCapacity || 0,
                      }),
                    },
                  },
                  ...(workSpace.isFull
                    ?
                      metadataAvatarProperty
                    : []),
                  {
                    icon: Clock,
                    property: {
                      type: 'status' as const,
                      value: {
                        label: workSpace.isFull
                          ? i18n.t('space_control.overview.is_full')
                          : i18n.t('settings.locations.location.work_spaces.available'),
                        status: workSpace.isFull ? 'critical' : 'positive',
                      },
                    },
                  },
                ]
              : [
                  {
                    icon: spaceUI.icon,
                    property: {
                      type: 'tag',
                      value: {
                        label: workSpace.workSpaceTypeName,
                      },
                    },
                  },
                  {
                    icon: Person,
                    property: {
                      type: 'text',
                      value: i18n.tp('settings.locations.location.work_spaces.n_people', {
                        count: workSpace.originalCapacity || 0,
                      }),
                    },
                  },
                ]
          }
        />
      )}
    </>
  )
}

export const WorkSpacesMapInternal = ({
  workAreaId,
  actions,
  date,
  endTime,
  startTime,
  mapMode,
  cardMode,
  map,
  workAreaMapLocations,
}: WorkSpacesMapProps) => {
  const mouse = useMouse()
  const [hovered, setHovered] = useState<WorkAreaMapLocationNode | null>(null)

  const { eventBus, isReady } = useMapEventBus()

  const [viewport, setViewport] = useState<PIXI.Container | null>(null)

  const handleSpaceHover = useCallback(
    ({ workAreaMapLocation }: { workAreaMapLocation: WorkAreaMapLocationNode | null }) => {
      setHovered(
        workAreaMapLocation
          ? workAreaMapLocations.find((location) => location.id === workAreaMapLocation.id) || null
          : null
      )
      actions.onPinHover?.({ workAreaMapLocation })
    },
    [workAreaMapLocations, actions]
  )

  useEffect(() => {
    if (!eventBus) return

    if (actions.onPinClick) eventBus.on(EVENT.SPACE_CLICK, actions.onPinClick)
    if (actions.onMapClick) eventBus.on(EVENT.MAP_CLICK, actions.onMapClick)
    eventBus.on(EVENT.SPACE_HOVER, handleSpaceHover)

    return () => {
      if (actions.onPinClick) eventBus.off(EVENT.SPACE_CLICK, actions.onPinClick)
      if (actions.onMapClick) eventBus.off(EVENT.MAP_CLICK, actions.onMapClick)
      eventBus.off(EVENT.SPACE_HOVER, handleSpaceHover)
    }
  }, [eventBus, actions.onPinClick, actions.onMapClick, handleSpaceHover])

  useEffect(() => {
    if (!eventBus || !isReady) return

    eventBus.emit(EVENT.UPDATE_WORKSPACES, {
      workAreaMapLocations: workAreaMapLocations,
    })
  }, [eventBus, workAreaMapLocations, isReady])

  useEffect(() => {
    if (!eventBus || !mapMode) return

    if (mapMode === MAP_MODE.PLACE_PIN) {
      eventBus.emit(EVENT.ACTIVATE_MOVE_CURSOR, {})
    } else {
      eventBus.emit(EVENT.RESET_CURSOR, {})
    }
  }, [mapMode, eventBus])

  if (!eventBus) return <LoadingState />
  if (!map || !map.image)
    return <EmptyState workAreaId={+workAreaId} locationId={map?.workArea?.location?.id || 0} />

  const getVerticalPosition = (mouseEvent: MouseEvent) => {
    const cardHeight = 310

    return mouseEvent?.clientY + cardHeight > window.innerHeight
      ? mouseEvent?.layerY - cardHeight
      : mouseEvent?.layerY + 10
  }

  const getHorizontalPosition = (mouseEvent: MouseEvent) => {
    return mouseEvent?.clientX + 186 >= window.innerWidth
      ? mouseEvent?.layerX - 400 + (window.innerWidth - mouseEvent?.clientX)
      : mouseEvent?.layerX - 186
  }

  return (
    <Box flexGrow style={{ position: 'relative', minHeight: '800px' }}>
      <Canvas>
        {mouse && hovered?.workSpace?.id && (
          <Box
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              top: getVerticalPosition(mouse).toString() + 'px',
              left: getHorizontalPosition(mouse).toString() + 'px',
            }}
            className="w-full max-w-[372px] shadow-xl rounded-lg"
          >
            <HoveredCard
              workSpaceId={hovered?.workSpace?.id}
              date={date}
              endTime={endTime}
              startTime={startTime}
              cardMode={cardMode ?? CARD_MODE.BOOKING}
            />
          </Box>
        )}
        <App mapImage={map.image} onViewportReady={setViewport} />
      </Canvas>
      <MapControls viewport={viewport} />
    </Box>
  )
}
